// src/components/satellite/SatelliteOverviewMap.jsx
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Edit3, Eye, Settings, Square, RotateCcw, Camera, RefreshCw, Smartphone, ArrowLeft, Check, Trash2, X } from 'lucide-react';
import { auth, db } from '../../utils/firebase';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import GoogleMapsSatelliteUpload from './GoogleMapsSatelliteUpload';

const SatelliteOverviewMap = ({ 
  clubId, 
  satelliteConfig, 
  onPitchClick, 
  isSetupMode = false,
  onEnterSetupMode,
  onSaveConfiguration,
  onSatelliteImageUpdate,
  existingPitches = [], // NEW: Pass existing pitches when editing
  isEditingFacility = false, // NEW: Flag to indicate editing vs new
  facilityName = '' // NEW: Name of facility being edited
}) => {
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const [clubName, setClubName] = useState('Loading...');
  
  // Mobile detection state
  const [isMobile, setIsMobile] = useState(false);

  // Satellite capture states
  const [showSatelliteCapture, setShowSatelliteCapture] = useState(false);
  const [isCapturingNew, setIsCapturingNew] = useState(false);

  // Drawing states for setup mode
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentDrawing, setCurrentDrawing] = useState(null);
  const [tempBoundaries, setTempBoundaries] = useState([]);

  // 4-corner drawing states (same as GoogleMapsSatelliteUpload)
  const [currentPoints, setCurrentPoints] = useState([]);
  const [pendingPitch, setPendingPitch] = useState(null);
  const [rotationAdjustment, setRotationAdjustment] = useState(0);

  // Popup states
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [pitchToDelete, setPitchToDelete] = useState(null);

  // NEW: Selected pitch for deletion
  const [selectedPitchIndex, setSelectedPitchIndex] = useState(null);
  
  // NEW: Track if we loaded existing pitches (to preserve numbering)
  const [loadedExistingPitches, setLoadedExistingPitches] = useState(false);
  const [originalPitchNumbers, setOriginalPitchNumbers] = useState([]);

  // CORNER EDITING: Track which corner is being dragged
  const [draggingCorner, setDraggingCorner] = useState(null); // { pitchIndex, cornerIndex } or null

  // Mobile detection logic
  useEffect(() => {
    const checkMobile = () => {
      const width = window.innerWidth;
      const userAgent = navigator.userAgent.toLowerCase();
      const isMobileDevice = /android|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
      const isSmallScreen = width < 768;
      
      setIsMobile(isMobileDevice || isSmallScreen);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // NEW: Load existing pitches when entering setup mode for editing
  useEffect(() => {
    if (isSetupMode && isEditingFacility && existingPitches.length > 0 && !loadedExistingPitches) {
      console.log('📦 Loading existing pitches for editing:', existingPitches);
      
      // Convert existing pitches to tempBoundaries format, preserving pitchNumber
      const loadedPitches = existingPitches.map(pitch => ({
        ...pitch,
        // Preserve original pitch number for later
        originalPitchNumber: pitch.pitchNumber,
        // Keep all the boundary data
        centerX: pitch.centerX,
        centerY: pitch.centerY,
        width: pitch.width,
        height: pitch.height,
        rotation: pitch.rotation,
        corners: pitch.corners,
        name: pitch.name || `Pitch ${pitch.pitchNumber}`
      }));
      
      setTempBoundaries(loadedPitches);
      setOriginalPitchNumbers(existingPitches.map(p => p.pitchNumber));
      setLoadedExistingPitches(true);
      
      console.log('✅ Loaded', loadedPitches.length, 'existing pitches for editing');
    }
  }, [isSetupMode, isEditingFacility, existingPitches, loadedExistingPitches]);

  // NEW: Reset loaded state when exiting setup mode
  useEffect(() => {
    if (!isSetupMode) {
      setLoadedExistingPitches(false);
      setSelectedPitchIndex(null);
      setDraggingCorner(null); // Reset corner dragging too
    }
  }, [isSetupMode]);

  // NEW: Delete a specific pitch by index
  const deletePitch = (index) => {
    console.log('🗑️ Deleting pitch at index:', index);
    const pitchToRemove = tempBoundaries[index];
    console.log('🗑️ Pitch being deleted:', pitchToRemove?.name || `Pitch ${index + 1}`);
    
    const newBoundaries = tempBoundaries.filter((_, i) => i !== index);
    setTempBoundaries(newBoundaries);
    setSelectedPitchIndex(null);
    setShowDeleteConfirm(false);
    setPitchToDelete(null);
  };

  // NEW: Confirm delete pitch
  const confirmDeletePitch = (index) => {
    setPitchToDelete(index);
    setShowDeleteConfirm(true);
  };

  // NEW: Cancel delete
  const cancelDelete = () => {
    setPitchToDelete(null);
    setShowDeleteConfirm(false);
  };

  // Fetch club name from Firebase
  useEffect(() => {
    let isMounted = true;
    
    const loadClubName = async () => {
      try {
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
          if (!currentUser) {
            if (isMounted) {
              setClubName('Club');
            }
            return;
          }

          try {
            const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
            if (!userDoc.exists()) {
              if (isMounted) {
                setClubName('Club');
              }
              return;
            }

            const userData = userDoc.data();
            const userClubId = userData.clubId;
            
            if (!userClubId) {
              if (isMounted) {
                setClubName('Club');
              }
              return;
            }

            const clubDoc = await getDoc(doc(db, 'clubs', userClubId));
            if (!clubDoc.exists()) {
              if (isMounted) {
                setClubName(`Club ${userClubId}`);
              }
              return;
            }

            const clubData = clubDoc.data();
            
            if (isMounted) {
              const foundName = clubData.name || `Club ${userClubId}`;
              setClubName(foundName);
            }
          } catch (err) {
            console.error('Error loading club name:', err);
            if (isMounted) {
              setClubName('Club');
            }
          }
        });

        return () => {
          isMounted = false;
          unsubscribe();
        };
      } catch (err) {
        console.error('Auth error:', err);
        if (isMounted) {
          setClubName('Club');
        }
      }
    };

    loadClubName();
  }, []);

  // Subscribe to satellite config changes
  useEffect(() => {
    if (!clubId) return;

    const clubRef = doc(db, 'clubs', clubId);
    const unsubscribe = onSnapshot(clubRef, (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        const newSatelliteConfig = data.satelliteConfig || null;
        
        if (newSatelliteConfig?.imageUrl !== satelliteConfig?.imageUrl || 
            newSatelliteConfig?.imageId !== satelliteConfig?.imageId) {
          setImageLoaded(false);
        }
        
        if (onSatelliteImageUpdate) {
          onSatelliteImageUpdate(newSatelliteConfig);
        }
      }
    });

    return () => unsubscribe();
  }, [clubId, satelliteConfig?.imageUrl, satelliteConfig?.imageId, onSatelliteImageUpdate]);

  const handleSatelliteImageSaved = async (imageData) => {
    try {
      setIsCapturingNew(true);
      setShowSatelliteCapture(false);
      setIsCapturingNew(false);
      setImageLoaded(false);
    } catch (error) {
      console.error('Error saving satellite image:', error);
      setIsCapturingNew(false);
    }
  };

  const handleSatelliteCaptureError = (error) => {
    console.error('Satellite capture error:', error);
    setIsCapturingNew(false);
  };

  const calculateCanvasSize = (imgWidth, imgHeight) => {
    const maxWidth = isMobile ? 350 : 1000;
    const maxHeight = isMobile ? 250 : 700;
    const aspectRatio = imgWidth / imgHeight;
    
    let width = maxWidth;
    let height = width / aspectRatio;
    
    if (height > maxHeight) {
      height = maxHeight;
      width = height * aspectRatio;
    }
    
    return { width, height };
  };

  const handleImageLoad = () => {
    if (imageRef.current && satelliteConfig) {
      const size = calculateCanvasSize(
        satelliteConfig.imageWidth, 
        satelliteConfig.imageHeight
      );
      setCanvasSize(size);
      setImageLoaded(true);
    }
  };

  const getMousePos = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    let clientX, clientY;
    if (e.touches && e.touches[0]) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }

    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY
    };
  };

  // Convert 4 corner points to rotated rectangle (same as GoogleMapsSatelliteUpload)
  const convertPolygonToRotatedRect = (points) => {
    // Sort points clockwise from centroid
    const centerX = points.reduce((sum, p) => sum + p.x, 0) / 4;
    const centerY = points.reduce((sum, p) => sum + p.y, 0) / 4;

    const sortedPoints = [...points].sort((a, b) => {
      const angleA = Math.atan2(a.y - centerY, a.x - centerX);
      const angleB = Math.atan2(b.y - centerY, b.x - centerX);
      return angleA - angleB;
    });

    // Calculate dimensions
    const edge1 = Math.sqrt(
      Math.pow(sortedPoints[1].x - sortedPoints[0].x, 2) + 
      Math.pow(sortedPoints[1].y - sortedPoints[0].y, 2)
    );
    const edge2 = Math.sqrt(
      Math.pow(sortedPoints[2].x - sortedPoints[1].x, 2) + 
      Math.pow(sortedPoints[2].y - sortedPoints[1].y, 2)
    );
    
    const width = edge1;
    const height = edge2;
    const rotation = Math.atan2(
      sortedPoints[1].y - sortedPoints[0].y,
      sortedPoints[1].x - sortedPoints[0].x
    );

    return {
      centerX,
      centerY,
      width,
      height,
      rotation,
      corners: sortedPoints,
      name: `Pitch ${tempBoundaries.length + 1}`
    };
  };

  // CORNER EDITING: Recalculate center, width, height, rotation from corners
  const recalculatePitchFromCorners = (corners, existingPitch) => {
    const centerX = corners.reduce((sum, p) => sum + p.x, 0) / 4;
    const centerY = corners.reduce((sum, p) => sum + p.y, 0) / 4;

    // Calculate dimensions from the new corners
    const edge1 = Math.sqrt(
      Math.pow(corners[1].x - corners[0].x, 2) + 
      Math.pow(corners[1].y - corners[0].y, 2)
    );
    const edge2 = Math.sqrt(
      Math.pow(corners[2].x - corners[1].x, 2) + 
      Math.pow(corners[2].y - corners[1].y, 2)
    );
    
    const rotation = Math.atan2(
      corners[1].y - corners[0].y,
      corners[1].x - corners[0].x
    );

    return {
      ...existingPitch,
      centerX,
      centerY,
      width: edge1,
      height: edge2,
      rotation,
      corners: corners
    };
  };

  // CORNER EDITING: Check if point is near a corner (within threshold)
  const findNearbyCorner = (pos, pitchIndex, threshold = 15) => {
    if (pitchIndex === null || pitchIndex === undefined) return null;
    
    const pitch = tempBoundaries[pitchIndex];
    if (!pitch?.corners || pitch.corners.length !== 4) return null;

    const canvas = canvasRef.current;
    if (!canvas || !satelliteConfig) return null;

    const scaleX = canvas.width / (satelliteConfig.imageWidth || 2048);
    const scaleY = canvas.height / (satelliteConfig.imageHeight || 1536);

    for (let i = 0; i < 4; i++) {
      const cornerX = pitch.corners[i].x * scaleX;
      const cornerY = pitch.corners[i].y * scaleY;
      const distance = Math.sqrt(Math.pow(pos.x - cornerX, 2) + Math.pow(pos.y - cornerY, 2));
      
      if (distance <= threshold) {
        return { pitchIndex, cornerIndex: i };
      }
    }
    return null;
  };

  // Drawing mode functions - 4 corner method
  const startDrawing = useCallback(() => {
    setIsDrawing(true);
    setCurrentPoints([]);
    setPendingPitch(null);
    setRotationAdjustment(0);
    setSelectedPitchIndex(null);
    setDraggingCorner(null); // Clear any corner drag state
  }, []);

  const cancelDrawing = () => {
    setIsDrawing(false);
    setCurrentPoints([]);
    setPendingPitch(null);
    setRotationAdjustment(0);
  };

  // Handle canvas click for 4-corner drawing
  const handleCanvasMouseDown = (e) => {
    if (!isSetupMode) return;
    
    const pos = getMousePos(e);
    if (!pos) return;

    // CORNER EDITING: First, check if clicking on a corner of the selected pitch
    if (!isDrawing && selectedPitchIndex !== null) {
      const corner = findNearbyCorner(pos, selectedPitchIndex, 20);
      if (corner) {
        console.log('🎯 Started dragging corner:', corner);
        setDraggingCorner(corner);
        e.preventDefault(); // Prevent other handlers
        return;
      }
    }
    
    // If not in drawing mode and not dragging a corner, check if clicking on existing pitch
    if (!isDrawing) {
      handlePitchSelection(e);
      return;
    }

    const newPoints = [...currentPoints, pos];
    setCurrentPoints(newPoints);

    // When we have 4 points, create the pitch
    if (newPoints.length === 4) {
      const newPitch = convertPolygonToRotatedRect(newPoints);
      setPendingPitch(newPitch);
      setRotationAdjustment(0);
      setIsDrawing(false);
    }
  };

  // Handle pitch selection when not drawing
  const handlePitchSelection = (e) => {
    if (tempBoundaries.length === 0) return;
    
    const pos = getMousePos(e);
    if (!pos) return;

    const canvas = canvasRef.current;
    const scaleX = canvas.width / (satelliteConfig.imageWidth || 2048);
    const scaleY = canvas.height / (satelliteConfig.imageHeight || 1536);

    // Check if click is inside any temp boundary
    for (let i = 0; i < tempBoundaries.length; i++) {
      const boundary = tempBoundaries[i];
      const centerX = boundary.centerX * scaleX;
      const centerY = boundary.centerY * scaleY;
      
      if (boundary.corners && boundary.corners.length === 4) {
        const scaledCorners = boundary.corners.map(c => ({ x: c.x * scaleX, y: c.y * scaleY }));
        if (isPointInPolygon(pos, scaledCorners)) {
          setSelectedPitchIndex(i === selectedPitchIndex ? null : i);
          return;
        }
      } else {
        const width = boundary.width * scaleX;
        const height = boundary.height * scaleY;
        
        const dx = pos.x - centerX;
        const dy = pos.y - centerY;
        const localX = dx * Math.cos(-boundary.rotation) - dy * Math.sin(-boundary.rotation);
        const localY = dx * Math.sin(-boundary.rotation) + dy * Math.cos(-boundary.rotation);

        if (Math.abs(localX) <= width / 2 && Math.abs(localY) <= height / 2) {
          setSelectedPitchIndex(i === selectedPitchIndex ? null : i);
          return;
        }
      }
    }
    
    // Clicked outside all pitches - deselect
    if (selectedPitchIndex !== null) {
      setSelectedPitchIndex(null);
    }
  };

  // Helper function for point-in-polygon check
  const isPointInPolygon = (point, polygon) => {
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i].x, yi = polygon[i].y;
      const xj = polygon[j].x, yj = polygon[j].y;
      
      const intersect = ((yi > point.y) !== (yj > point.y)) &&
        (point.x < (xj - xi) * (point.y - yi) / (yj - yi) + xi);
      if (intersect) inside = !inside;
    }
    return inside;
  };

  // Confirm pending pitch
  const handleConfirmPitch = () => {
    if (!pendingPitch) return;
    
    // Apply rotation adjustment if any
    const finalPitch = {
      ...pendingPitch,
      rotation: pendingPitch.rotation + (rotationAdjustment * Math.PI / 180)
    };
    
    setTempBoundaries([...tempBoundaries, finalPitch]);
    setPendingPitch(null);
    setRotationAdjustment(0);
    setCurrentPoints([]);
  };

  // Cancel pending pitch
  const handleCancelPitch = () => {
    setPendingPitch(null);
    setRotationAdjustment(0);
    setCurrentPoints([]);
    setIsDrawing(true); // Go back to drawing mode
  };

  // CORNER EDITING: Handle mouse move for corner dragging
  const handleCanvasMouseMove = (e) => {
    if (!draggingCorner) return;
    
    const pos = getMousePos(e);
    if (!pos) return;

    const canvas = canvasRef.current;
    if (!canvas || !satelliteConfig) return;

    // Convert screen position back to image coordinates
    const scaleX = canvas.width / (satelliteConfig.imageWidth || 2048);
    const scaleY = canvas.height / (satelliteConfig.imageHeight || 1536);

    const newX = pos.x / scaleX;
    const newY = pos.y / scaleY;

    // Update the corner position in tempBoundaries
    setTempBoundaries(prev => {
      const updated = [...prev];
      const pitch = updated[draggingCorner.pitchIndex];
      
      if (pitch?.corners) {
        const newCorners = [...pitch.corners];
        newCorners[draggingCorner.cornerIndex] = { x: newX, y: newY };
        
        // Recalculate the pitch properties from the new corners
        updated[draggingCorner.pitchIndex] = recalculatePitchFromCorners(newCorners, pitch);
      }
      
      return updated;
    });
  };

  // CORNER EDITING: Handle mouse up to stop dragging
  const handleCanvasMouseUp = (e) => {
    if (draggingCorner) {
      console.log('🎯 Finished dragging corner');
      setDraggingCorner(null);
    }
  };

  const confirmClearAllPitches = () => {
    setTempBoundaries([]);
    setShowClearConfirm(false);
    setSelectedPitchIndex(null);
  };

  const cancelClearAllPitches = () => {
    setShowClearConfirm(false);
  };

  const handleSaveSetup = () => {
    if (tempBoundaries.length === 0) {
      alert('Please draw at least one pitch boundary before saving.');
      return;
    }

    if (onSaveConfiguration) {
      // When editing existing facility, number pitches 1, 2, 3... (not continuing from global max)
      // This ensures "Pitch 1" stays "Pitch 1" when you edit the facility
      const pitchesToSave = tempBoundaries.map((pitch, index) => {
        const pitchNumber = index + 1;
        // Create a clean object without the temporary originalPitchNumber field
        const { originalPitchNumber, ...cleanPitch } = pitch;
        return {
          ...cleanPitch,
          id: `pitch-${pitchNumber}`,
          name: `Pitch ${pitchNumber}`,
          pitchNumber: pitchNumber
        };
      });
      
      console.log('💾 Saving pitches with numbers:', pitchesToSave.map(p => p.pitchNumber));
      onSaveConfiguration(pitchesToSave);
    }
  };

  const getTotalPitchCount = () => {
    return tempBoundaries.length;
  };

  // Canvas drawing
  useEffect(() => {
    if (!imageLoaded || !canvasRef.current || !satelliteConfig) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw image
    if (imageRef.current) {
      ctx.drawImage(imageRef.current, 0, 0, canvas.width, canvas.height);
    }

    // Draw existing pitches (non-setup mode)
    if (!isSetupMode && satelliteConfig.pitches) {
      const scaleX = canvas.width / (satelliteConfig.imageWidth || 2048);
      const scaleY = canvas.height / (satelliteConfig.imageHeight || 1536);

      satelliteConfig.pitches.forEach((pitch) => {
        const centerX = pitch.centerX * scaleX;
        const centerY = pitch.centerY * scaleY;

        // If pitch has corners (new format), draw as polygon
        if (pitch.corners && pitch.corners.length === 4) {
          ctx.fillStyle = 'rgba(59, 130, 246, 0.35)';
          ctx.strokeStyle = '#3b82f6';
          ctx.lineWidth = 2;
          
          ctx.beginPath();
          ctx.moveTo(pitch.corners[0].x * scaleX, pitch.corners[0].y * scaleY);
          for (let i = 1; i < 4; i++) {
            ctx.lineTo(pitch.corners[i].x * scaleX, pitch.corners[i].y * scaleY);
          }
          ctx.closePath();
          ctx.fill();
          ctx.stroke();
        } else {
          // Legacy format: draw as rotated rectangle
          const width = pitch.width * scaleX;
          const height = pitch.height * scaleY;

          ctx.save();
          ctx.translate(centerX, centerY);
          ctx.rotate(pitch.rotation);

          // Blue fill with border
          ctx.fillStyle = 'rgba(59, 130, 246, 0.35)';
          ctx.strokeStyle = '#3b82f6';
          ctx.lineWidth = 2;
          ctx.fillRect(-width / 2, -height / 2, width, height);
          ctx.strokeRect(-width / 2, -height / 2, width, height);

          ctx.restore();
        }

        // Label
        ctx.fillStyle = '#1f2937';
        ctx.font = 'bold 16px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(pitch.name || pitch.id, centerX, centerY);
      });
    }

    // Draw temp boundaries (setup mode)
    if (isSetupMode && tempBoundaries.length > 0) {
      const scaleX = canvas.width / (satelliteConfig.imageWidth || 2048);
      const scaleY = canvas.height / (satelliteConfig.imageHeight || 1536);

      tempBoundaries.forEach((boundary, index) => {
        const centerX = boundary.centerX * scaleX;
        const centerY = boundary.centerY * scaleY;
        const isSelected = selectedPitchIndex === index;

        // If boundary has corners, draw as polygon
        if (boundary.corners && boundary.corners.length === 4) {
          // Selected pitch gets orange/red highlight, others get blue
          ctx.fillStyle = isSelected ? 'rgba(239, 68, 68, 0.4)' : 'rgba(59, 130, 246, 0.3)';
          ctx.strokeStyle = isSelected ? '#dc2626' : '#3b82f6';
          ctx.lineWidth = isSelected ? 4 : 2;
          
          ctx.beginPath();
          ctx.moveTo(boundary.corners[0].x * scaleX, boundary.corners[0].y * scaleY);
          for (let i = 1; i < 4; i++) {
            ctx.lineTo(boundary.corners[i].x * scaleX, boundary.corners[i].y * scaleY);
          }
          ctx.closePath();
          ctx.fill();
          ctx.stroke();

          // CORNER EDITING: Draw corner handles on selected pitch
          if (isSelected) {
            boundary.corners.forEach((corner, cornerIdx) => {
              const cx = corner.x * scaleX;
              const cy = corner.y * scaleY;
              const isBeingDragged = draggingCorner?.pitchIndex === index && draggingCorner?.cornerIndex === cornerIdx;
              
              // White outer circle
              ctx.fillStyle = 'white';
              ctx.beginPath();
              ctx.arc(cx, cy, isBeingDragged ? 12 : 10, 0, 2 * Math.PI);
              ctx.fill();
              
              // Colored inner circle
              ctx.fillStyle = isBeingDragged ? '#ef4444' : '#3b82f6';
              ctx.strokeStyle = isBeingDragged ? '#b91c1c' : '#1d4ed8';
              ctx.lineWidth = 2;
              ctx.beginPath();
              ctx.arc(cx, cy, isBeingDragged ? 10 : 8, 0, 2 * Math.PI);
              ctx.fill();
              ctx.stroke();
            });
          }
        } else {
          // Legacy format
          const width = boundary.width * scaleX;
          const height = boundary.height * scaleY;

          ctx.save();
          ctx.translate(centerX, centerY);
          ctx.rotate(boundary.rotation);

          ctx.fillStyle = isSelected ? 'rgba(239, 68, 68, 0.4)' : 'rgba(59, 130, 246, 0.3)';
          ctx.strokeStyle = isSelected ? '#dc2626' : '#3b82f6';
          ctx.lineWidth = isSelected ? 4 : 2;
          ctx.fillRect(-width / 2, -height / 2, width, height);
          ctx.strokeRect(-width / 2, -height / 2, width, height);

          ctx.restore();
        }

        // Label (applies to both polygon and rectangle)
        ctx.fillStyle = isSelected ? '#dc2626' : '#1f2937';
        ctx.font = isSelected ? 'bold 18px sans-serif' : 'bold 16px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(boundary.name, centerX, centerY);
      });
    }

    // Draw current corner points being clicked (4-corner drawing)
    if (isSetupMode && isDrawing && currentPoints.length > 0) {
      const scaleX = canvas.width / (satelliteConfig.imageWidth || 2048);
      const scaleY = canvas.height / (satelliteConfig.imageHeight || 1536);

      // Draw lines between points
      if (currentPoints.length > 1) {
        ctx.strokeStyle = '#3b82f6';
        ctx.lineWidth = 2;
        ctx.setLineDash([5, 5]);
        ctx.beginPath();
        ctx.moveTo(currentPoints[0].x * scaleX, currentPoints[0].y * scaleY);
        for (let i = 1; i < currentPoints.length; i++) {
          ctx.lineTo(currentPoints[i].x * scaleX, currentPoints[i].y * scaleY);
        }
        ctx.stroke();
        ctx.setLineDash([]);
      }

      // Draw corner points as circles - bigger and more visible
      currentPoints.forEach((point, index) => {
        // White outline for visibility
        ctx.fillStyle = 'white';
        ctx.beginPath();
        ctx.arc(point.x * scaleX, point.y * scaleY, 14, 0, 2 * Math.PI);
        ctx.fill();
        
        // Blue fill
        ctx.fillStyle = '#3b82f6';
        ctx.beginPath();
        ctx.arc(point.x * scaleX, point.y * scaleY, 12, 0, 2 * Math.PI);
        ctx.fill();
        
        // Number inside
        ctx.fillStyle = 'white';
        ctx.font = 'bold 14px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText((index + 1).toString(), point.x * scaleX, point.y * scaleY);
      });
    }

    // Draw pending pitch preview (after 4 corners clicked)
    if (isSetupMode && pendingPitch) {
      const scaleX = canvas.width / (satelliteConfig.imageWidth || 2048);
      const scaleY = canvas.height / (satelliteConfig.imageHeight || 1536);

      if (pendingPitch.corners && pendingPitch.corners.length === 4) {
        // Apply rotation adjustment for preview
        const adjustedRotation = rotationAdjustment * Math.PI / 180;
        const adjustedCorners = pendingPitch.corners.map(corner => {
          const dx = corner.x - pendingPitch.centerX;
          const dy = corner.y - pendingPitch.centerY;
          const cos = Math.cos(adjustedRotation);
          const sin = Math.sin(adjustedRotation);
          return {
            x: pendingPitch.centerX + (dx * cos - dy * sin),
            y: pendingPitch.centerY + (dx * sin + dy * cos)
          };
        });

        ctx.fillStyle = 'rgba(34, 197, 94, 0.4)';
        ctx.strokeStyle = '#10b981';
        ctx.lineWidth = 3;
        ctx.setLineDash([5, 5]);
        
        ctx.beginPath();
        ctx.moveTo(adjustedCorners[0].x * scaleX, adjustedCorners[0].y * scaleY);
        for (let i = 1; i < 4; i++) {
          ctx.lineTo(adjustedCorners[i].x * scaleX, adjustedCorners[i].y * scaleY);
        }
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
        ctx.setLineDash([]);
      }
    }
  }, [
    imageLoaded,
    satelliteConfig,
    tempBoundaries,
    isSetupMode,
    isDrawing,
    currentPoints,
    pendingPitch,
    rotationAdjustment,
    selectedPitchIndex,
    draggingCorner // CORNER EDITING: Re-render when dragging
  ]);

  // Handle pitch click in navigation mode only
  const handleCanvasClick = (e) => {
    // Navigation mode - handle pitch click for navigation
    if (!isSetupMode && satelliteConfig?.pitches && onPitchClick) {
      const pos = getMousePos(e);
      if (!pos) return;

      const canvas = canvasRef.current;
      const scaleX = canvas.width / (satelliteConfig.imageWidth || 1600);
      const scaleY = canvas.height / (satelliteConfig.imageHeight || 1200);

      // Check if click is inside any pitch
      for (const pitch of satelliteConfig.pitches) {
        const centerX = pitch.centerX * scaleX;
        const centerY = pitch.centerY * scaleY;
        const width = pitch.width * scaleX;
        const height = pitch.height * scaleY;

        // Transform click point to pitch's local coordinate system
        const dx = pos.x - centerX;
        const dy = pos.y - centerY;
        const localX = dx * Math.cos(-pitch.rotation) - dy * Math.sin(-pitch.rotation);
        const localY = dx * Math.sin(-pitch.rotation) + dy * Math.cos(-pitch.rotation);

        // Check if point is inside rectangle
        if (Math.abs(localX) <= width / 2 && Math.abs(localY) <= height / 2) {
          onPitchClick(pitch.pitchId);
          return;
        }
      }
    }
  };

  if (!satelliteConfig?.imageUrl) {
    return (
      <div style={{
        padding: '40px',
        textAlign: 'center',
        backgroundColor: '#f3f4f6',
        borderRadius: '8px',
        border: '2px dashed #d1d5db'
      }}>
        <Camera size={48} color="#9ca3af" style={{ marginBottom: '16px' }} />
        <h3 style={{ color: '#6b7280', marginBottom: '8px' }}>No Satellite Image</h3>
        <p style={{ color: '#9ca3af', marginBottom: '16px' }}>
          Please capture a satellite image to view your pitches
        </p>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      {/* Hidden image for loading */}
      <img
        ref={imageRef}
        src={satelliteConfig.imageUrl}
        alt="Satellite"
        style={{ display: 'none' }}
        onLoad={handleImageLoad}
        crossOrigin="anonymous"
      />

      {/* Canvas */}
      <div style={{ position: 'relative', display: 'inline-block' }}>
        <canvas
          ref={canvasRef}
          width={satelliteConfig.imageWidth || 1600}
          height={satelliteConfig.imageHeight || 1200}
          style={{
            width: canvasSize.width || '100%',
            height: canvasSize.height || 'auto',
            border: '2px solid #e5e7eb',
            borderRadius: '8px',
            cursor: draggingCorner ? 'grabbing' : (isSetupMode && isDrawing ? 'crosshair' : (isSetupMode && selectedPitchIndex !== null ? 'grab' : 'pointer')),
            touchAction: 'none'
          }}
          onMouseDown={handleCanvasMouseDown}
          onMouseMove={handleCanvasMouseMove}
          onMouseUp={handleCanvasMouseUp}
          onMouseLeave={handleCanvasMouseUp}
          onClick={handleCanvasClick}
          onTouchStart={handleCanvasMouseDown}
          onTouchMove={handleCanvasMouseMove}
          onTouchEnd={handleCanvasMouseUp}
        />

        {!imageLoaded && (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            color: '#6b7280'
          }}>
            Loading image...
          </div>
        )}
      </div>

      {/* Setup Mode Controls */}
      {isSetupMode && (
        <div style={{
          marginTop: '20px',
          padding: '16px',
          backgroundColor: 'white',
          borderRadius: '8px',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: '12px',
            flexWrap: 'wrap'
          }}>
            {/* Pending pitch confirmation - shown after 4 corners clicked */}
            {pendingPitch && (
              <>
                <button
                  onClick={handleConfirmPitch}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    flex: isMobile ? '1' : '0'
                  }}
                >
                  <Check size={16} />
                  Confirm Pitch
                </button>
                <button
                  onClick={handleCancelPitch}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#ef4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    flex: isMobile ? '1' : '0'
                  }}
                >
                  <X size={16} />
                  Cancel
                </button>
                
                {/* Rotation adjustment */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginLeft: isMobile ? '0' : 'auto'
                }}>
                  <label style={{ fontSize: '14px', color: '#374151' }}>
                    Fine-tune rotation:
                  </label>
                  <input
                    type="range"
                    min="-15"
                    max="15"
                    value={rotationAdjustment}
                    onChange={(e) => setRotationAdjustment(Number(e.target.value))}
                    style={{ width: '120px' }}
                  />
                  <span style={{ fontSize: '14px', color: '#6b7280', minWidth: '40px' }}>
                    {rotationAdjustment}°
                  </span>
                </div>
              </>
            )}

            {/* Normal controls - when not confirming a pending pitch */}
            {!pendingPitch && (
              <>
                {!isDrawing ? (
                  <button
                    onClick={startDrawing}
                    style={{
                      padding: '12px 24px',
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <Square size={16} />
                    Draw New Pitch
                  </button>
                ) : (
                  <button
                    onClick={cancelDrawing}
                    style={{
                      padding: '12px 24px',
                      backgroundColor: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <X size={16} />
                    Cancel Drawing
                  </button>
                )}

                {tempBoundaries.length > 0 && (
                  <>
                    <button
                      onClick={() => setShowClearConfirm(true)}
                      style={{
                        padding: '12px 24px',
                        backgroundColor: '#f3f4f6',
                        color: '#374151',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      <RotateCcw size={16} />
                      Clear All
                    </button>

                    <button
                      onClick={handleSaveSetup}
                      style={{
                        padding: '12px 24px',
                        backgroundColor: '#10b981',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        marginLeft: isMobile ? '0' : 'auto'
                      }}
                    >
                      <Check size={16} />
                      Save {getTotalPitchCount()} Pitch{getTotalPitchCount() !== 1 ? 'es' : ''}
                    </button>
                  </>
                )}
              </>
            )}
          </div>

          {/* Drawing instructions */}
          {isDrawing && (
            <div style={{
              marginTop: '16px',
              padding: '12px',
              backgroundColor: '#eff6ff',
              border: '1px solid #bfdbfe',
              borderRadius: '6px',
              fontSize: '14px',
              color: '#1e40af'
            }}>
              <strong>Click 4 corners</strong> of the pitch in clockwise order ({currentPoints.length}/4 placed)
            </div>
          )}

          {/* CORNER EDITING: Instructions when pitch is selected */}
          {!isDrawing && !pendingPitch && selectedPitchIndex !== null && (
            <div style={{
              marginTop: '16px',
              padding: '12px',
              backgroundColor: '#fef3c7',
              border: '1px solid #fcd34d',
              borderRadius: '6px',
              fontSize: '14px',
              color: '#92400e'
            }}>
              <strong>Pitch selected:</strong> Drag the corner handles to adjust the boundary, or click Delete to remove.
            </div>
          )}

          {/* Pitch list */}
          {tempBoundaries.length > 0 && !isDrawing && !pendingPitch && (
            <div style={{
              marginTop: '16px',
              padding: '12px',
              backgroundColor: '#f9fafb',
              borderRadius: '6px',
              border: '1px solid #e5e7eb'
            }}>
              <h4 style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '12px'
              }}>
                Drawn Pitches ({tempBoundaries.length})
              </h4>
              
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}>
                {tempBoundaries.map((boundary, index) => (
                  <div 
                    key={index}
                    onClick={() => setSelectedPitchIndex(index === selectedPitchIndex ? null : index)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '10px 12px',
                      backgroundColor: selectedPitchIndex === index ? '#fef2f2' : 'white',
                      border: selectedPitchIndex === index ? '2px solid #dc2626' : '1px solid #e5e7eb',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    <span style={{
                      fontSize: '14px',
                      fontWeight: selectedPitchIndex === index ? '600' : '500',
                      color: selectedPitchIndex === index ? '#dc2626' : '#374151'
                    }}>
                      {boundary.name}
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        confirmDeletePitch(index);
                      }}
                      style={{
                        padding: '6px 10px',
                        backgroundColor: '#fee2e2',
                        color: '#dc2626',
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      <Trash2 size={14} />
                      Delete
                    </button>
                  </div>
                ))}
              </div>
              
              {selectedPitchIndex !== null && (
                <div style={{
                  marginTop: '12px',
                  padding: '10px',
                  backgroundColor: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: '6px',
                  fontSize: '13px',
                  color: '#991b1b',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <span>
                    <strong>{tempBoundaries[selectedPitchIndex]?.name}</strong> selected
                  </span>
                  <button
                    onClick={() => confirmDeletePitch(selectedPitchIndex)}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: '#dc2626',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      fontSize: '13px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    <Trash2 size={14} />
                    Delete Selected
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Navigation Mode Info */}
      {!isSetupMode && (
        <div style={{
          marginTop: '12px',
          padding: '12px',
          backgroundColor: '#f0fdf4',
          border: '1px solid #86efac',
          borderRadius: '6px',
          fontSize: '14px',
          color: '#166534',
          textAlign: 'center'
        }}>
          <Eye size={16} style={{ display: 'inline', marginRight: '6px', verticalAlign: 'middle' }} />
          Click on any pitch to view and manage allocations
        </div>
      )}

      {/* Popups */}
      {showClearConfirm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '32px',
            maxWidth: '400px',
            margin: '20px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
          }}>
            <h3 style={{
              fontSize: '20px',
              fontWeight: '600',
              color: '#1f2937',
              marginBottom: '16px',
              textAlign: 'center'
            }}>
              Clear All Pitches?
            </h3>
            
            <p style={{
              color: '#374151',
              marginBottom: '24px',
              textAlign: 'center'
            }}>
              Remove all {getTotalPitchCount()} pitch{getTotalPitchCount() !== 1 ? 'es' : ''}?
            </p>
            
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={cancelClearAllPitches}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#e5e7eb',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmClearAllPitches}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Yes, Clear
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Single Pitch Confirmation */}
      {showDeleteConfirm && pitchToDelete !== null && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '32px',
            maxWidth: '420px',
            margin: '20px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              backgroundColor: '#fee2e2',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px'
            }}>
              <Trash2 size={24} color="#dc2626" />
            </div>
            
            <h3 style={{
              fontSize: '20px',
              fontWeight: '600',
              color: '#1f2937',
              marginBottom: '12px',
              textAlign: 'center'
            }}>
              Delete {tempBoundaries[pitchToDelete]?.name || `Pitch ${pitchToDelete + 1}`}?
            </h3>
            
            <p style={{
              color: '#6b7280',
              marginBottom: '24px',
              textAlign: 'center',
              fontSize: '14px',
              lineHeight: '1.5'
            }}>
              This will remove the pitch from your facility. 
              {tempBoundaries.length > 1 && ' Remaining pitches will be renumbered.'}
            </p>
            
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'center'
            }}>
              <button
                onClick={cancelDelete}
                style={{
                  padding: '10px 24px',
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '500',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => deletePitch(pitchToDelete)}
                style={{
                  padding: '10px 24px',
                  backgroundColor: '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '500',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <Trash2 size={16} />
                Delete Pitch
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SatelliteOverviewMap;
